import { Button, ButtonGroup, Card, Heading, IconButton, LuIcon, Menu, Separator, toast } from "@overmap-ai/blocks"
import {
	EMPTY_ARRAY,
	ERROR_MSG_UNEXPECTED,
	OrganizationAccessLevel,
	pluralize,
	toFileNameSafeString,
	type User,
} from "@overmap-ai/common"
import {
	APIError,
	selectAccessibleProjects,
	selectCurrentUser,
	selectFormById,
	selectFormIdentifiersOfForm,
	selectFormSubmissionsOfForm,
	selectFormSubscribersOfForm,
	selectLatestFormRevisionOfForm,
	selectUsersOfOrganization,
} from "@overmap-ai/core"
import { useOpenState } from "@overmap-ai/hooks"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import type { InitialTableState, SortingState, Updater, VisibilityState } from "@tanstack/react-table"
import { saveAs } from "file-saver"
import Papa from "papaparse"
import { memo, useCallback, useMemo } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { FormSubmissionPdfExportOptionsDialog } from "../../components/Dialogs/FormSubmissionPdfExportOptionsDialog"
import { UserDropdownCheckboxMenu } from "../../components/DropdownCheckboxMenus/UserDropdownCheckboxMenu"
import { FormSubmissionDataFilterMenu } from "../../components/FilterMenus/FormSubmissionDataFilterMenu"
import { useDeserializeFieldSchema } from "../../components/Form/hooks"
import { UserIcon } from "../../components/Icons/UserIcon"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { useFormSubmissionTable, useFormSubmissionTableColumns } from "../../components/Tables"
import { ColumnVisibilityDropdown, PaginationControl } from "../../components/Tables/components"
import { defaultRowsPerPageOptions } from "../../components/Tables/constants"
import { tableIconMapping } from "../../components/Tables/mappings"
import { Table } from "../../components/Tables/Table"
import { getFormSubmissionDataCsvExporter } from "../../csv/CSVExporter/forms"
import { FilterRenderer } from "../../filtering/components"
import type { AnyFilter } from "../../filtering/filter"
import { useFilterManager, useFilters } from "../../filtering/hooks"
import { getFormSubmissionTableFilterSchema } from "../../filtering/schemas"
import { useFormSubmissionData } from "../../hooks/forms"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import {
	setFormSubmissionColumnSorting,
	setFormSubmissionColumnVisibility,
	setFormSubmissionFilters,
	useAppDispatch,
	useAppSelector,
	useShallowEqaulSelector,
} from "../../store"
import { selectColumnSortingOfForm, selectColumnVisibilityOfForm, selectFormFiltersOfForm } from "../../store/selectors"
import type { FormSubmissionData } from "../../typings"
import type { FormSubmissionPdfExportOptions } from "../../typings/reports"

const initialTableState: InitialTableState = {
	sorting: [
		{
			id: "submitted_at",
			desc: true,
		},
	],
	pagination: {
		pageSize: defaultRowsPerPageOptions[0],
	},
}

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/forms/$formId/")

export const FormDetailView = memo(() => {
	const { organizationId, formId } = routeApi.useParams()

	const form = useAppSelector(selectFormById(formId))

	if (!form) {
		throw new Error("Expected form to exist.")
	}

	const currentUser = useAppSelector(selectCurrentUser)!
	const latestFormRevision = useAppSelector(selectLatestFormRevisionOfForm(form.uuid))
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const formIdentifiers = useShallowEqaulSelector(selectFormIdentifiersOfForm(form.uuid))
	const serializedFilters = useAppSelector(selectFormFiltersOfForm(form.uuid))
	const columnSorting = useAppSelector(selectColumnSortingOfForm(form.uuid))
	const columnVisibility = useAppSelector(selectColumnVisibilityOfForm(form.uuid))
	const formSubmissions = useAppSelector(selectFormSubmissionsOfForm(form.uuid))
	const formSubmissionData = useFormSubmissionData(formSubmissions)
	const users = useShallowEqaulSelector(selectUsersOfOrganization(organizationId))
	const projects = useShallowEqaulSelector(selectAccessibleProjects(currentUser.id))
	const formSubscribers = useShallowEqaulSelector(selectFormSubscribersOfForm(form.uuid))

	const handleEdit = useCallback(() => {
		void navigate({
			from: "/$organizationId/forms/$formId/",
			to: "/$organizationId/settings/forms/$formId",
		})
	}, [navigate])

	const handleNewEntry = useCallback(() => {
		void navigate({
			from: "/$organizationId/forms/$formId/",
			to: "/$organizationId/forms/$formId/submit",
		})
	}, [navigate])

	const handleSubmissionClick = useCallback(
		(formSubmissionData: FormSubmissionData) => {
			const { formSubmission } = formSubmissionData

			void navigate({
				from: "/$organizationId/forms/$formId/",
				to: "/$organizationId/forms/submissions/$formSubmissionId",
				params: (prev) => ({
					...prev,
					formSubmissionId: formSubmission.uuid,
				}),
			})
		},
		[navigate],
	)

	const fieldSchema = useDeserializeFieldSchema(latestFormRevision?.fields ?? EMPTY_ARRAY)

	const filterSchema = useMemo(
		() => getFormSubmissionTableFilterSchema({ formIdentifiers, fieldSchema, users, projects }),
		[fieldSchema, formIdentifiers, users, projects],
	)

	const filters = useMemo(() => {
		return filterSchema.deserializeFilters(serializedFilters)
	}, [filterSchema, serializedFilters])

	const handleFiltersChange = useCallback(
		(filters: AnyFilter[]) => {
			dispatch(
				setFormSubmissionFilters({
					id: form.uuid,
					filters: filterSchema.serializeFilters(filters),
				}),
			)
		},
		[dispatch, filterSchema, form.uuid],
	)

	const handleColumnVisibilityChange = useCallback(
		(updater: Updater<VisibilityState>) => {
			dispatch(
				setFormSubmissionColumnVisibility({
					id: form.uuid,
					columnVisibility: typeof updater === "function" ? updater(columnVisibility) : updater,
				}),
			)
		},
		[dispatch, form.uuid, columnVisibility],
	)

	const handleSortingChange = useCallback(
		(updater: Updater<SortingState>) => {
			dispatch(
				setFormSubmissionColumnSorting({
					id: form.uuid,
					columnSorting: typeof updater === "function" ? updater(columnSorting) : updater,
				}),
			)
		},
		[dispatch, form.uuid, columnSorting],
	)

	const filterManager = useFilterManager({
		filterSchema,
		filters,
		onFilterChange: handleFiltersChange,
	})

	const filteredSubmissions = useFilters({
		data: formSubmissionData,
		filters: filters,
		filterManager: filterManager,
	})

	const columns = useFormSubmissionTableColumns({
		formIdentifiers: formIdentifiers,
		fieldSchema: fieldSchema,
		issues: EMPTY_ARRAY,
		assets: EMPTY_ARRAY,
		projects: projects,
	})

	const table = useFormSubmissionTable({
		columns: columns,
		data: filteredSubmissions,
		enableRowSelection: true,
		initialState: initialTableState,
		state: {
			columnVisibility,
			sorting: columnSorting,
		},
		onColumnVisibilityChange: handleColumnVisibilityChange,
		onSortingChange: handleSortingChange,
	})

	const [showPdfExportOptionsDialog, openPdfExportOptionsDialog, closePdfExportOptionsDialog] = useOpenState(false)

	const handleExportToPdf = useCallback(
		(options: FormSubmissionPdfExportOptions) => {
			const selectedRows = table.getSelectedRowModel().rows.map((row) => row.id)

			void sdk.formSubmissions
				.getFormSubmissionsPdfReport(form.uuid, selectedRows, options)
				.then(() => {
					toast.success({
						title: "Report requested",
						description: `Requested report for ${selectedRows.length} ${pluralize("submission", selectedRows.length)}. Visit the Reports page when it is ready.`,
					})
				})
				.catch((error: unknown) => {
					toast.error({
						title: "Failed to generate report",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})
		},
		[table, form.uuid],
	)

	const exportToCSV = useCallback(() => {
		const exporter = getFormSubmissionDataCsvExporter(
			table.getSortedRowModel().rows.map((row) => row.original),
			{
				formIdentifiers: formIdentifiers,
				formFieldSchema: fieldSchema,
				users: users,
			},
		)

		const csvData = Papa.unparse(exporter.toPapaUnparseFormat())
		const blob = new Blob([csvData], { type: "text/csv" })
		saveAs(blob, toFileNameSafeString(form.name, ".csv"))
	}, [table, fieldSchema, formIdentifiers, form.name, users])

	const subscribedUsers = useMemo(() => {
		const userIdsSet = new Set(formSubscribers.map(({ user }) => user))
		return users.filter(({ id }) => userIdsSet.has(id))
	}, [formSubscribers, users])

	const handleCheckedUserChange = useCallback(
		(user: User, checked: boolean) => {
			if (checked) {
				void sdk.formSubscribers.add({
					user: user.id,
					form: form.uuid,
				})
			} else {
				const formSubscriber = formSubscribers.find((formSubscriber) => formSubscriber.user === user.id)
				if (!formSubscriber) return
				void sdk.formSubscribers.delete(formSubscriber.uuid)
			}
		},
		[formSubscribers, form.uuid],
	)

	return (
		<ViewContainer>
			<div className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex h-max w-full items-end justify-between gap-2">
					<Heading size="xl" className="truncate">
						{form.name}
					</Heading>
					<ButtonGroup className="flex items-center gap-2" variant="soft" size="sm" accentColor="base">
						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
							<Button onClick={handleNewEntry} accentColor="primary" variant="surface" size="sm">
								<LuIcon icon={modelIconMapping.add} />
								Entry
							</Button>
						</OrganizationAccessRequired>

						<Menu.Root>
							<Menu.ClickTrigger>
								<IconButton variant="ghost">
									<LuIcon icon={modelIconMapping.more} />
								</IconButton>
							</Menu.ClickTrigger>
							<Menu.Content size="sm">
								<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
									<Menu.Item onClick={handleNewEntry}>
										<LuIcon icon={modelIconMapping.add} />
										Entry
									</Menu.Item>
								</OrganizationAccessRequired>

								<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
									<Menu.Item onClick={handleEdit}>
										<LuIcon icon={modelIconMapping.edit} />
										Edit
									</Menu.Item>
								</OrganizationAccessRequired>
							</Menu.Content>
						</Menu.Root>
					</ButtonGroup>
				</div>

				<Separator size="full" />

				<Card className="flex max-h-full w-full flex-col gap-2 overflow-hidden" size="sm">
					<ButtonGroup
						className="flex w-full justify-between gap-2"
						accentColor="base"
						size="xs"
						variant="soft"
					>
						<div className="flex items-center gap-2">
							<Heading>Submissions</Heading>
							<FormSubmissionDataFilterMenu
								filterManager={filterManager}
								fieldSchema={fieldSchema}
								formIdentifiers={formIdentifiers}
								users={users}
								projects={projects}
								align="start"
								size="sm"
							>
								<Button>
									<LuIcon icon={modelIconMapping.filter} />
									Filter
								</Button>
							</FormSubmissionDataFilterMenu>

							{filterManager.getFilters().length > 0 && (
								<IconButton onClick={filterManager.removeAll}>
									<LuIcon icon={modelIconMapping.close} />
								</IconButton>
							)}
						</div>

						<div className="flex items-center gap-2">
							{table.getSelectedRowModel().rows.length > 0 && (
								<>
									<Menu.Root>
										<Menu.ClickTrigger>
											<Button>
												<LuIcon icon={modelIconMapping.command} />
												Actions
											</Button>
										</Menu.ClickTrigger>
										<Menu.Content size="sm">
											<Menu.Item onSelect={openPdfExportOptionsDialog}>
												<LuIcon icon={modelIconMapping.download} />
												Export PDF
											</Menu.Item>
											<Menu.Item onSelect={exportToCSV}>
												<LuIcon icon={modelIconMapping.download} />
												Export CSV
											</Menu.Item>
										</Menu.Content>
									</Menu.Root>
								</>
							)}

							<ColumnVisibilityDropdown table={{ ...table }} align="end" size="sm">
								<IconButton>
									<LuIcon icon={tableIconMapping.columns} />
								</IconButton>
							</ColumnVisibilityDropdown>
						</div>
					</ButtonGroup>

					<FilterRenderer filterManager={filterManager} size="sm" />

					<div className="max-h-full w-full overflow-auto">
						<Table
							compact
							className="w-full"
							size="sm"
							variant="surface"
							table={{ ...table }}
							onRowClick={handleSubmissionClick}
							interactive
							emptyText="No submissions."
						/>
					</div>

					<PaginationControl table={{ ...table }} />
				</Card>

				<div className="flex justify-end gap-2">
					<UserDropdownCheckboxMenu
						users={users}
						checkedUsers={subscribedUsers}
						onCheckedUserChange={handleCheckedUserChange}
						size="sm"
						align="end"
					>
						<Button variant="ghost" size="sm" className="w-max" accentColor="base">
							{subscribedUsers.length > 0
								? subscribedUsers.slice(0, 3).map((user) => {
										return <UserIcon key={user.id} userId={user.id} size="xs" />
									})
								: "Subscribers"}
						</Button>
					</UserDropdownCheckboxMenu>
				</div>
			</div>
			<FormSubmissionPdfExportOptionsDialog
				key={form.uuid}
				formId={form.uuid}
				onSubmitOptions={handleExportToPdf}
				open={showPdfExportOptionsDialog}
				onOpenChange={closePdfExportOptionsDialog}
			/>
		</ViewContainer>
	)
})

FormDetailView.displayName = "FormDetailView"
